import { useEffect, useState, useCallback } from 'react';
import { mcpAuthApi } from '../../services/mcpAuthApi';
import './IdeIntegrationsTab.css';

const MCP_BASE_URL = import.meta.env.VITE_MCP_URL || 'http://localhost:3100';

function formatRelative(dateStr) {
  if (!dateStr) return 'never';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

const TOKEN_MASK = '•'.repeat(32);

function buildRemoteConfig(sessionToken, { reveal }) {
  return JSON.stringify(
    {
      mcpServers: {
        omnicontext: {
          serverUrl: `${MCP_BASE_URL}/mcp`,
          headers: {
            Authorization: `Bearer ${reveal ? sessionToken : TOKEN_MASK}`,
          },
        },
      },
    },
    null,
    2,
  );
}

function buildLocalCliConfig() {
  // Bearer never appears in this snippet — Antigravity (and other MCP clients
  // that support env-var substitution in the `env` block) expands
  // ${OMC_SESSION_TOKEN} from the shell environment at IDE start.
  return JSON.stringify(
    {
      mcpServers: {
        omnicontext: {
          command: 'npx',
          args: ['-y', 'omnicontext-mcp'],
          env: {
            OMNICONTEXT_API_URL: 'http://localhost:8000',
            OMC_SESSION_TOKEN: '${OMC_SESSION_TOKEN}',
          },
        },
      },
    },
    null,
    2,
  );
}

function buildExportLine(sessionToken, { reveal }) {
  return `export OMC_SESSION_TOKEN='${reveal ? sessionToken : TOKEN_MASK}'`;
}

export default function IdeIntegrationsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [deviceLabel, setDeviceLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // { session_token, session_id, expires_at }
  const [revealed, setRevealed] = useState(false);
  const [configMode, setConfigMode] = useState('remote'); // 'remote' | 'local-cli'
  const [copied, setCopied] = useState(null); // 'token' | 'config' | 'export' | null

  const refresh = useCallback(async () => {
    try {
      const res = await mcpAuthApi.listSessions();
      setSessions(res?.sessions ?? []);
      setError(null);
    } catch (e) {
      setError(e.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleGenerate() {
    try {
      setError(null);
      setGenerating(true);
      const label = deviceLabel.trim() || null;
      const res = await mcpAuthApi.createSession({ deviceLabel: label });
      setGenerated(res);
      setRevealed(false);
      setDeviceLabel('');
      await refresh();
    } catch (e) {
      setError(e.message ?? String(e));
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm('Revoke this IDE session? It will need to re-pair.')) return;
    try {
      await mcpAuthApi.revokeSession(id);
      await refresh();
    } catch (e) {
      setError(e.message ?? String(e));
    }
  }

  function copyToClipboard(text, key) {
    if (!text) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
      })
      .catch(() => {/* swallow */});
  }

  return (
    <div className="ide-integrations-tab">
      <h3>IDE Integrations</h3>
      {error && <div className="error" role="alert">{error}</div>}

      {!generated ? (
        <div className="generate-row">
          <h4>Pair a new IDE with your account</h4>
          <label>
            Optional label:
            <input
              type="text"
              maxLength={64}
              placeholder="VS Code @ laptop"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              disabled={generating}
            />
          </label>
          <button
            onClick={handleGenerate}
            className="generate-btn"
            disabled={generating}
          >
            {generating ? 'Generating…' : 'Generate IDE session'}
          </button>
        </div>
      ) : (
        <div className="pending-code">
          <h4>Your IDE session is ready</h4>
          <p className="step-help">
            The session token below is the only place this value will be shown.
            Paste it into your IDE&apos;s MCP config now; if you lose it, revoke
            this session and generate a new one.
          </p>

          <div className="mode-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={configMode === 'remote'}
              className={`mode-tab ${configMode === 'remote' ? 'active' : ''}`}
              onClick={() => setConfigMode('remote')}
            >
              Remote URL
            </button>
            <button
              role="tab"
              aria-selected={configMode === 'local-cli'}
              className={`mode-tab ${configMode === 'local-cli' ? 'active' : ''}`}
              onClick={() => setConfigMode('local-cli')}
            >
              Local CLI <span className="badge">no secret in file</span>
            </button>
          </div>

          {configMode === 'remote' ? (
            <>
              <p className="step-help">
                Paste this into your IDE&apos;s MCP config. The bearer token sits
                in the file — protect it like any API key (chmod 600, no commits,
                no cloud sync).
              </p>
              <div className="step-header">
                <strong>MCP config</strong>
                <span className="header-actions">
                  <button
                    className="copy-btn"
                    onClick={() => setRevealed((v) => !v)}
                    aria-pressed={revealed}
                  >
                    {revealed ? 'Hide' : 'Show'}
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() =>
                      copyToClipboard(buildRemoteConfig(generated.session_token, { reveal: true }), 'config')
                    }
                  >
                    {copied === 'config' ? 'Copied!' : 'Copy config'}
                  </button>
                </span>
              </div>
              <pre className="pair-snippet">
                {buildRemoteConfig(generated.session_token, { reveal: revealed })}
              </pre>

              <div className="step-header" style={{ marginTop: '0.8rem' }}>
                <strong>Just the bearer token</strong>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(generated.session_token, 'token')}
                >
                  {copied === 'token' ? 'Copied!' : 'Copy token'}
                </button>
              </div>
              <pre className="pair-snippet">
                {revealed ? generated.session_token : TOKEN_MASK}
              </pre>
            </>
          ) : (
            <>
              <p className="step-help">
                Bearer token stays out of the IDE config and lives in your shell
                environment instead. Requires the <code>omnicontext-mcp</code>{' '}
                package installed (or use{' '}
                <code>command: &quot;node&quot;, args: [&quot;/abs/path/dist/index.js&quot;]</code>
                {' '}during local dev).
              </p>

              <div className="step-header">
                <strong>1. Add to your shell profile (~/.zshrc or ~/.bashrc)</strong>
                <span className="header-actions">
                  <button
                    className="copy-btn"
                    onClick={() => setRevealed((v) => !v)}
                    aria-pressed={revealed}
                  >
                    {revealed ? 'Hide' : 'Show'}
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() =>
                      copyToClipboard(buildExportLine(generated.session_token, { reveal: true }), 'export')
                    }
                  >
                    {copied === 'export' ? 'Copied!' : 'Copy export'}
                  </button>
                </span>
              </div>
              <pre className="pair-snippet">
                {buildExportLine(generated.session_token, { reveal: revealed })}
              </pre>

              <div className="step-header" style={{ marginTop: '0.8rem' }}>
                <strong>2. Paste this MCP config into your IDE</strong>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(buildLocalCliConfig(), 'config')}
                >
                  {copied === 'config' ? 'Copied!' : 'Copy config'}
                </button>
              </div>
              <pre className="pair-snippet">{buildLocalCliConfig()}</pre>

              <p className="step-help" style={{ marginTop: '0.6rem' }}>
                After updating <code>~/.zshrc</code>, open a new terminal (or
                <code> source ~/.zshrc</code>) and restart your IDE so it
                inherits the new env var.
              </p>
            </>
          )}

          <button
            className="cancel-btn"
            onClick={() => { setGenerated(null); setRevealed(false); }}
          >
            Done
          </button>
        </div>
      )}

      <div className="active-sessions">
        <h4>Active sessions</h4>
        {loading ? (
          <div>Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="empty">No paired IDEs yet.</div>
        ) : (
          <ul>
            {sessions.map((s) => (
              <li key={s.id} className="session-row">
                <div className="session-meta">
                  <strong>{s.device_label || '(unlabelled)'}</strong>
                  <div className="session-detail">
                    Paired {formatRelative(s.paired_at)} ·
                    Last used {formatRelative(s.last_used)} ·
                    {s.ip || '—'}
                  </div>
                </div>
                <button className="revoke-btn" onClick={() => handleRevoke(s.id)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
